// WP3: generic source of nav-dropdown CHILDREN for modules that have modes.
//
// Before this file, `subMenus` for a nav item were hand-typed directly in
// App.tsx's setUpNav switch (`case "inventory":`, `case "financereports":`),
// duplicating each module's own mode vocabulary (its `modes.ts` /
// `breadcrumbMenu`). That is exactly how Inventory's nav list drifted out of
// sync — it was hand-typed as 8 entries while InventoryLayout.tsx's own
// `breadcrumbMenu` had grown a 9th ("Transfers"). See recon/A-mode-switchers.md
// §4 and §6.
//
// This registry maps a nav key (the `function` value for a plain-function nav
// row, or `remote_menu_key` for a url/custom row) to that module's own mode
// list, read directly from the module's exported mode config. Add an entry
// here — not another hand-typed array in App.tsx — when a new mode-bearing
// module should surface its views as nav children.
import type { SubMenuItem } from "./types";
import { modeSubMenus } from "./navGates";
import { MODE_LABELS as adsAndSbsModeLabels } from "../ads-and-sbs/modes";
import { breadcrumbMenu as inventoryBreadcrumbMenu } from "../inventory/InventoryLayout";
import { breadcrumbMenuItems as financialReportsBreadcrumbMenuItems } from "../financial-reports";
import { MODE_LABELS as quotesModeLabels, hasModeSwitcher as quotesHasModeSwitcher } from "../quotes/modes";
import { MODE_LABELS as modificationsModeLabels } from "../modifications/modes";
import { MODE_STATUS as stockTrackerModeStatus, FALLBACK_LABELS as stockTrackerFallbackLabels } from "../cycle-count/modes";
import { MODE_LABELS as actionRegisterModeLabels } from "../action_register/modes";
import { MODE_LABELS as aircraftRosterModeLabels } from "../aircraft_roster/modes";
import { MODE_LABELS as operationsReportsModeLabels } from "../operations-reports/modes";
import { MODE_LABELS as flightScheduleModeLabels } from "../flight_schedule/modes";
import { MODE_LABELS as coreModeLabels } from "../core/modes";
import { MODE_LABELS as userManagementModeLabels } from "../users/user-management/modes";
import { MODE_LABELS as groupsModeLabels } from "../groups/modes";
import {
  MODE_LABELS as flightDutyModeLabels,
  ADMIN_MODE_LABELS as flightDutyAdminModeLabels,
  MODE_GATES as flightDutyModeGates,
} from "../flight-duty/modes";
import { MODE_LABELS as flightsModeLabels } from "../flights/modes";
import { MODE_LABELS as timesheetsModeLabels, MODE_GATES as timesheetsModeGates } from "../timesheets/modes";
import { breadcrumbMenu as companiesBreadcrumbMenu } from "../companies/CompaniesLayout";
import { breadcrumbMenu as componentBreadcrumbMenu } from "../component/ComponentLayout";
import { breadcrumbMenu as defectBreadcrumbMenu } from "../defect-reporting/DefectLayout";
import { breadcrumbMenu as subscriptionsBreadcrumbMenu } from "../subscriptions/SubscriptionsLayout";
import { breadcrumbMenu as fleetBreadcrumbMenu } from "../fleet/FleetLayout";
import { breadcrumbMenu as techLogBreadcrumbMenu } from "../tech-log/TechLogLayout";
import { breadcrumbMenu as trendBreadcrumbMenu } from "../trend/TrendLayout";
import { MODE_LABELS as bookingsModeLabels } from "../bookings/modes";
import { MODE_LABELS as pilotRosterModeLabels } from "../pilot_roster/modes";
import { MODE_LABELS as flightExpensesModeLabels } from "../flight-expenses/FlightExpensesLayout";
import { MODE_LABELS as fuelModeLabels } from "../fuel_tanks/modes";
import { breadcrumbMenu as opportunitiesBreadcrumbMenu } from "../opportunities/OpportunityLayout";
import { MODE_LABELS as toolRequestModeLabels } from "../tool-requests/modes";
import { MODE_LABELS as personnelModeLabels } from "../personnel/modes";
import { MODE_LABELS as xeroModeLabels } from "../xero/modes";
import { MODE_LABELS as ataModeLabels } from "../ata/modes";
import { MODE_LABELS as supplierInvoicesModeLabels, hasModeSwitcher as supplierInvoicesHasModeSwitcher } from "../supplier-invoices/modes";
import { MODE_LABELS as ordersModeLabels, hasModeSwitcher as ordersHasModeSwitcher } from "../orders/modes";
import { breadcrumbMenu as manifestBreadcrumbMenu } from "../manifest/ManifestLayout";

/** "/inventory/parts" -> "parts" (the relative child-route segment). */
const lastSegment = (path: string): string => path.split("/").filter(Boolean).pop() ?? path;

const inventorySubMenus: SubMenuItem[] = Object.entries(inventoryBreadcrumbMenu).map(
  ([path, item]) => ({ name: item.label, to: lastSegment(path) })
);

const financialReportsSubMenus: SubMenuItem[] = financialReportsBreadcrumbMenuItems.map(
  (item) => ({ name: item.label, to: item.to })
);

const adsAndSbsSubMenus: SubMenuItem[] = Object.entries(adsAndSbsModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

const modificationsSubMenus: SubMenuItem[] = Object.entries(modificationsModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

const actionRegisterSubMenus: SubMenuItem[] = Object.entries(actionRegisterModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

const aircraftRosterSubMenus: SubMenuItem[] = Object.entries(aircraftRosterModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

const flightScheduleSubMenus: SubMenuItem[] = Object.entries(flightScheduleModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

const operationsReportsSubMenus: SubMenuItem[] = Object.entries(
  operationsReportsModeLabels
).map(([segment, label]) => ({ name: label, to: segment }));

const coreSubMenus: SubMenuItem[] = Object.entries(coreModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

const userManagementSubMenus: SubMenuItem[] = Object.entries(
  userManagementModeLabels
).map(([segment, label]) => ({ name: label, to: segment }));

const groupsSubMenus: SubMenuItem[] = Object.entries(groupsModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Gated entries (Crew Tracker, Rules) carry `gate`; Menu.tsx renders each only
// for a user its gate lets in (PATTERNS.md BP-90, H131, H172).
const flightDutySubMenus: SubMenuItem[] = modeSubMenus(
  { ...flightDutyModeLabels, ...flightDutyAdminModeLabels },
  flightDutyModeGates
);

const flightsSubMenus: SubMenuItem[] = Object.entries(
  flightsModeLabels
).map(([segment, label]) => ({ name: label, to: segment }));

// Team / Report / Codes are gated (BP-90, H131).
const timesheetsSubMenus: SubMenuItem[] = modeSubMenus(timesheetsModeLabels, timesheetsModeGates);

// Opportunities — derived from the layout's own vocab rather than
// hand-typed, so a third mode (or a re-ordering) can't drift the nav list out
// of sync with the breadcrumb switcher the way Inventory's did. Keys are
// absolute paths ("/opportunities/list"), same shape as Inventory/Fleet/
// Component, so this needs the lastSegment() mapping, not a plain
// Object.entries map.
const opportunitiesSubMenus: SubMenuItem[] = Object.entries(opportunitiesBreadcrumbMenu).map(
  ([path, label]) => ({ name: label, to: lastSegment(path) })
);

// Cycle Count (Stores -> Cycle Count). Nav children render with no live vocab
// context, so they use the module's fallback labels — same labels the layout
// itself falls back to when the tenant's config vocab is unseeded.
const stockTrackerSubMenus: SubMenuItem[] = Object.keys(stockTrackerModeStatus).map(
  (slug) => ({ name: stockTrackerFallbackLabels[slug], to: slug })
);

// Tool Requests & Usage (Stores -> Tool Requests).
const toolRequestSubMenus: SubMenuItem[] = Object.entries(toolRequestModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Personnel (Flight Admin -> Personnel / Operations).
const personnelSubMenus: SubMenuItem[] = Object.entries(personnelModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Quotes' Process mode is g2-only (see quotes/modes.ts) — every other tenant's
// Quotes mount has no /process route at all, so an unconditional entry here
// would 404 the nav child on every non-g2 tenant. Empty array on those
// products is a deliberate no-op, not an oversight.
const quotesSubMenus: SubMenuItem[] = quotesHasModeSwitcher()
  ? Object.entries(quotesModeLabels).map(([segment, label]) => ({ name: label, to: segment }))
  : [];

// Companies (Address Book) — segment-keyed vocab, plain Object.entries map.
const companiesSubMenus: SubMenuItem[] = Object.entries(companiesBreadcrumbMenu).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Component — vocab keys are absolute paths ("/components/parts"), same
// shape as Inventory, so this needs the lastSegment() mapping.
const componentSubMenus: SubMenuItem[] = Object.entries(componentBreadcrumbMenu).map(
  ([path, label]) => ({ name: label, to: lastSegment(path) })
);

// Defect Reporting — segment-keyed vocab.
const defectSubMenus: SubMenuItem[] = Object.entries(defectBreadcrumbMenu).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Subscriptions — segment-keyed vocab (same shape as Defect Reporting).
const subscriptionsSubMenus: SubMenuItem[] = Object.entries(subscriptionsBreadcrumbMenu).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Fleet — vocab keys are absolute paths ("/fleet/all"), needs lastSegment().
const fleetSubMenus: SubMenuItem[] = Object.entries(fleetBreadcrumbMenu).map(
  ([path, label]) => ({ name: label, to: lastSegment(path) })
);

// Tech Log — segment-keyed vocab.
const techLogSubMenus: SubMenuItem[] = Object.entries(techLogBreadcrumbMenu).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Trend — segment-keyed vocab.
const trendSubMenus: SubMenuItem[] = Object.entries(trendBreadcrumbMenu).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Bookings — modes.ts already exists, segment-keyed vocab.
const bookingsSubMenus: SubMenuItem[] = Object.entries(bookingsModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Pilot Roster — modes.ts already exists, segment-keyed vocab.
const pilotRosterSubMenus: SubMenuItem[] = Object.entries(pilotRosterModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Flight Expenses — segment-keyed vocab.
const flightExpensesSubMenus: SubMenuItem[] = Object.entries(
  flightExpensesModeLabels
).map(([segment, label]) => ({ name: label, to: segment }));

// Fuel (OPS Flight Admin) — segment-keyed vocab. The module KEY is
// `fuel_tanks` (mod_function) even though the DISPLAY name is "Fuel"; this
// registry is keyed by the raw nav key, so `fuel_tanks` is the right spelling
// here and the label comes from the nav row itself.
const fuelSubMenus: SubMenuItem[] = Object.entries(fuelModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Xero Integration — segment-keyed vocab.
const xeroSubMenus: SubMenuItem[] = Object.entries(xeroModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// ATA — Dashboard, Chapters, Tasks.
const ataSubMenus: SubMenuItem[] = Object.entries(ataModeLabels).map(
  ([segment, label]) => ({ name: label, to: segment })
);

// Supplier Invoices — List / Bill Process (g2-only).
const supplierInvoicesSubMenus: SubMenuItem[] = supplierInvoicesHasModeSwitcher()
  ? Object.entries(supplierInvoicesModeLabels).map(([segment, label]) => ({ name: label, to: segment }))
  : [];

// Purchase Orders (Purchasing -> Purchase Orders) — List / Process (g2-only).
const ordersSubMenus: SubMenuItem[] = ordersHasModeSwitcher()
  ? Object.entries(ordersModeLabels).map(([segment, label]) => ({ name: label, to: segment }))
  : [];

// Pax Manifest (Flight Admin -> Pax Manifest) — Dispatch Board / Passenger Register.
const manifestSubMenus: SubMenuItem[] = Object.entries(manifestBreadcrumbMenu).map(
  ([segment, label]) => ({ name: label, to: segment })
);

/**
 * Keyed by nav `function` (plain-function rows) OR `remote_menu_key`
 * (url/custom rows) — App.tsx looks this up by whichever one the row's
 * branch resolved against, same as the two hand-typed cases it replaces.
 *
 * The lookup is by EXACT raw nav key, so a module reachable under several
 * Features.tsx aliases needs one line per alias.
 */
export const NAV_MODE_REGISTRY: Record<string, SubMenuItem[]> = {
  orders: ordersSubMenus,
  purchase_orders: ordersSubMenus,
  "purchase-orders": ordersSubMenus,
  purchaseorders: ordersSubMenus,
  PurchaseOrders: ordersSubMenus,
  "Purchase Orders": ordersSubMenus,
  Orders: ordersSubMenus,
  manifest: manifestSubMenus,
  "pax-manifest": manifestSubMenus,
  pax_manifest: manifestSubMenus,
  passenger_manifest: manifestSubMenus,
  "Pax Manifest": manifestSubMenus,
  "Passenger Manifesting": manifestSubMenus,
  supplier_invoices: supplierInvoicesSubMenus,
  "supplier-invoices": supplierInvoicesSubMenus,
  inventory: inventorySubMenus,
  // Financial Reports arrives on g2/gcip as `function: "job_costs"` —
  // Features.tsx registers the module under that name, and that is the key
  // the function-keyed branch of setUpNav looks up.
  job_costs: financialReportsSubMenus,
  financereports: financialReportsSubMenus,
  financial_reports: financialReportsSubMenus,
  "financial-reports": financialReportsSubMenus,
  financialreports: financialReportsSubMenus,
  jobcosts: financialReportsSubMenus,
  "job-costs": financialReportsSubMenus,
  // ADs and SBs — modes come from the module's own vocabulary file, which
  // both of its mounts already share.
  "ads-and-sbs": adsAndSbsSubMenus,
  ads_and_sbs: adsAndSbsSubMenus,
  // Quotes — covers Customer Quotes, Supplier Quotes, and all route aliases.
  customer_quotes: quotesSubMenus,
  supplier_quotes: quotesSubMenus,
  "supplier-quotes": quotesSubMenus,
  "customer-quotes": quotesSubMenus,
  quotes: quotesSubMenus,
  // Opportunities (Sales -> Opportunities)
  opportunities: opportunitiesSubMenus,
  opportunity: opportunitiesSubMenus,
  // Modifications (Tech Records -> Modifications)
  modifications: modificationsSubMenus,
  modification: modificationsSubMenus,
  aircraft_modifications: modificationsSubMenus,
  "aircraft-modifications": modificationsSubMenus,
  // Tool Requests (Stores -> Tool Requests)
  toolrequests: toolRequestSubMenus,
  service_requests: toolRequestSubMenus,
  tool_requests: toolRequestSubMenus,
  "tool-requests": toolRequestSubMenus,
  // Cycle Count — Features.tsx registers CycleCount under the nav function
  // `stock_tracker` (mod_function is dual-purpose: route key AND
  // config/permission key, see src/cycle-count/index.tsx).
  stock_tracker: stockTrackerSubMenus,
  cycle_count: stockTrackerSubMenus,
  "cycle-count": stockTrackerSubMenus,
  // Action Register — Features.tsx registers this module under actionregister.
  actionregister: actionRegisterSubMenus,
  action_register: actionRegisterSubMenus,
  "action-register": actionRegisterSubMenus,
  // Aircraft Roster (OPS Flight Admin) — Features.tsx key "aircraft_roster".
  aircraft_roster: aircraftRosterSubMenus,
  "aircraft-roster": aircraftRosterSubMenus,
  // Flight Schedule (OPS flight-ops cluster) — Features.tsx key "flight_schedule".
  flight_schedule: flightScheduleSubMenus,
  "flight-schedule": flightScheduleSubMenus,
  // Operations Reports (OPS / g2 Flight Admin) — Features.tsx key "operations_reports".
  operations_reports: operationsReportsSubMenus,
  "operations-reports": operationsReportsSubMenus,
  // Core Returns & Tracking — Features.tsx registers this module under BOTH
  // "core" and "cores" (src/app/Features.tsx:319-320), same component.
  core: coreSubMenus,
  cores: coreSubMenus,
  // User Management (Directory) — Features.tsx registers this module under
  // the single key "user_management" (src/app/Features.tsx:162), matching
  // App.tsx's DEFAULT_G2_NAV fallback entry for the Directory section.
  user_management: userManagementSubMenus,
  // Groups — Features.tsx / App.tsx DEFAULT_G2_NAV register this module
  // under the single key "groups". List/Matrix restored 2026-08-25 (Isaac);
  // see src/groups/modes.ts.
  groups: groupsSubMenus,
  // Flight & Duty (OPS Flight Admin / Pilots) — Features.tsx key "flightduty".
  flightduty: flightDutySubMenus,
  flight_duty: flightDutySubMenus,
  "flight-duty": flightDutySubMenus,
  // Flights (OPS Flight Admin / Pilots) — Features.tsx key "flights".
  flights: flightsSubMenus,
  // Pilot Timesheets (Pilots) — Features.tsx key "timesheets".
  timesheets: timesheetsSubMenus,
  pilot_timesheets: timesheetsSubMenus,
  time: timesheetsSubMenus,
  timesheet: timesheetsSubMenus,
  "time-sheets": timesheetsSubMenus,
  "pilot-timesheets": timesheetsSubMenus,
  // Reports and Forms (module_stationery) — deliberately NOT here. It has
  // exactly one listing view (ReportsAndFormsList); its "layer" chip toggles
  // which template set renders, not which component does, so it is a MINOR
  // module (PATTERNS.md §"Module weight") with no mode switcher and nothing
  // for this registry to attach.

  // Companies (Address Book) — the live g2 nav row is a `remote_menu_key`
  // (its `function` is "url"), so the key is `AddressBook`, not a
  // Features.tsx alias. Read from the live g2 /v6/nav payload.
  AddressBook: companiesSubMenus,
  companies: companiesSubMenus,
  address_book: companiesSubMenus,
  "address-book": companiesSubMenus,
  // Component (Tech Records) — same as Companies: live row is
  // `remote_menu_key` because its `function` is "url".
  components: componentSubMenus,
  // Defect Reporting — live nav `function`.
  defect: defectSubMenus,
  defect_reporting: defectSubMenus,
  "defect-reporting": defectSubMenus,
  defects: defectSubMenus,
  // Fleet — live nav `function`.
  fleet: fleetSubMenus,
  // Subscriptions — nav `function` seeded by upgrade-notes version-21/02.
  subscriptions: subscriptionsSubMenus,
  // Tech Log — live nav `function`.
  techlog: techLogSubMenus,
  tech_log: techLogSubMenus,
  "tech-log": techLogSubMenus,
  // Trend — live nav `function`.
  trend: trendSubMenus,
  engine_trend: trendSubMenus,
  "engine-trend": trendSubMenus,
  // Bookings (Flight Booking) — live nav `function`.
  flight_booking: bookingsSubMenus,
  bookings: bookingsSubMenus,
  booking: bookingsSubMenus,
  "flight-booking": bookingsSubMenus,
  // Pilot Roster — live nav `function`.
  pilot_roster: pilotRosterSubMenus,
  "pilot-roster": pilotRosterSubMenus,
  // Flight Expenses — live nav `function`.
  flight_expenses: flightExpensesSubMenus,
  "flight-expenses": flightExpensesSubMenus,
  // Personnel (Flight Admin -> Personnel)
  personnel: personnelSubMenus,
  // Fuel — live nav `function` is the mod_function `fuel_tanks`
  fuel_tanks: fuelSubMenus,
  fuel: fuelSubMenus,
  "fuel-tanks": fuelSubMenus,
  // Xero Integration — Features.tsx keys "xero" and "finance/xero".
  xero: xeroSubMenus,
  "finance/xero": xeroSubMenus,
  // ATA — Dashboard, ATA Chapters, Tasks
  ata: ataSubMenus,
  "ata-chapters": ataSubMenus,
  ata_chapters: ataSubMenus,
  ata_tasks: ataSubMenus,
  "ata-tasks": ataSubMenus,
};

/**
 * Attach a module's mode list to a nav item as `subMenus`, if the registry
 * has an entry for its key and it doesn't already carry one. Safe to call on
 * every nav item unconditionally (live nav rows AND the DEFAULT_G2_NAV
 * fallback) — a miss is just a no-op. Mutates in place (matches the
 * mutate-then-continue style setUpNav already uses on `menuItem.to` /
 * `menuItem.name` in the same loop) and also returns the item, so it can be
 * used either as a statement or inline in an object-construction expression.
 */
export function applyNavModeChildren<
  T extends { subMenus?: ReadonlyArray<SubMenuItem> }
>(menuItem: T, navKey: string): T {
  if (!menuItem.subMenus || menuItem.subMenus.length === 0) {
    const subMenus = NAV_MODE_REGISTRY[navKey];
    if (subMenus && subMenus.length > 0) {
      menuItem.subMenus = subMenus;
    }
  }
  return menuItem;
}
